import { NavigationStart, Router, RouterOutlet } from '@angular/router';
import { UserInfoService } from '@core/services/user-info.service';
import { filter, map, Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { DomSanitizer } from '@angular/platform-browser';
import { MatDialog } from '@angular/material/dialog';
import { userPartnerLogoUrlSelector } from '@core/store/selectors/user-profile.selectors';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { AutoLogoutService } from '@core/services/auto-logout.service';
import { AutoLogoutComponent } from '@shared/components/dialogs/auto-logout/auto-logout.component';
import { SideNavigation } from './side-navigation';
import { SideNavigationConfig } from './side-navigation.config';
import { SupportFaqDialogComponent } from '@shared/components/dialogs/support-faq-dialog/support-faq-dialog.component';
import { selectEmergencyUrl } from '@core/store/selectors/app-config.selectors';
import { APIService } from '@core/services/api/api.service';
import { selectBffBaseUrl } from '@core/store/selectors/api-config.selectors';
import { userOrganizationSelector } from '@core/store/selectors/user-fhir.selectors';
import { SUPPORT_TOPICS } from '@shared/constants/support-topics';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import {
  PageActionHeaderComponent,
  PageLayoutComponent,
  SideNavComponent,
  SideNavigationList,
  SideNavigationListItem,
  SideNavModule,
} from '@xt/ui-kit';

import {
  accountTerminationStatusSelector,
  userAcronymSelector,
  userEmailSelector,
  userFullNameSelector,
} from '@core/store/selectors/user-data.selectors';

import {
  isUserRolePatientSelector,
  isUserRoleProviderSelector,
} from '@core/store/selectors/user-role.selectors';

import {
  Component,
  inject,
  OnInit,
  SecurityContext,
  Signal,
  ViewChild,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { HeaderComponent } from './header/header.component';
import { MatSidenavContainer } from '@angular/material/sidenav';
import { TerminateUserAccountBannerComponent } from '@features/profile/terminate-user-account-banner/terminate-user-account-banner.component';
import { TranslocoDirective } from '@jsverse/transloco';

const ROUTE_LAYOUT_SETTINGS = {
  noPadding: ['identification', 'verification'],
  pageActiveStyle: ['profile'],
};

@Component({
  selector: 'xt-portal-dashboard-shell',
  templateUrl: './dashboard-shell.component.html',
  styleUrls: ['./dashboard-shell.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    HeaderComponent,
    MatSidenavContainer,
    SideNavModule,
    PageLayoutComponent,
    PageActionHeaderComponent,
    RouterOutlet,
    TerminateUserAccountBannerComponent,
    TranslocoDirective
],
  providers: [OnlineStatusService],
})
export class DashboardShellComponent implements OnInit {
  @ViewChild('sidenav') sidenav: SideNavComponent;

  // Injects
  private readonly sideNavigation = inject(SideNavigation);
  private readonly sideNavigationConfig = inject(SideNavigationConfig);
  private readonly apiService = inject(APIService);

  private readonly userInfoService = inject(UserInfoService);
  private readonly onlineStatusService = inject(OnlineStatusService);
  private readonly domSanitizer = inject(DomSanitizer);
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly autoLogoutService = inject(AutoLogoutService);

  // Variables

  public readonly navigationList$: Observable<SideNavigationList[]> =
    this.sideNavigation.navList$;
  public readonly navigationBottomList: SideNavigationListItem[] =
    this.sideNavigationConfig.bottomNavigationConfig;

  public readonly partnerLogoUrl$ = this.store.pipe(
    select(userPartnerLogoUrlSelector),
  );
  public readonly name$ = this.store.pipe(select(userFullNameSelector));
  public readonly acronym$ = this.store.pipe(select(userAcronymSelector));
  public readonly email$ = this.store.pipe(select(userEmailSelector));
  public readonly selectEmergencyUrl$ = this.store.pipe(selectEmergencyUrl);

  public readonly isUserRolePatient$ = this.store.pipe(
    select(isUserRolePatientSelector),
  );

  public readonly isUserRoleProvider$ = this.store.pipe(
    select(isUserRoleProviderSelector),
  );

  public readonly onlineStatus$ = this.onlineStatusService.status.pipe(
    map((status: OnlineStatusType) => status),
  );

  public isUserAccountTerminated$ = this.store.pipe(
    select(accountTerminationStatusSelector),
    map((status) => !!status?.terminated),
  );

  private readonly getLayoutSetting = (item: string[]) =>
    item.some((item) => this.router.url.includes(item));
  public readonly noPaddingMode = this.getLayoutSetting(
    ROUTE_LAYOUT_SETTINGS.noPadding,
  );
  public readonly pageActiveStyle = this.getLayoutSetting(
    ROUTE_LAYOUT_SETTINGS.pageActiveStyle,
  );

  public readonly hasVerifiedRole: Signal<boolean> = toSignal(
    this.userInfoService.hasVerifiedRole$,
    { initialValue: false },
  );

  private readonly closeMenuOnNavigationStartEffect = this.router.events
    .pipe(
      takeUntilDestroyed(),
      filter((event) => event instanceof NavigationStart),
    )
    .subscribe(() => this.closeSidenav());

  ngOnInit(): void {
    document.body.style.backgroundColor = this.domSanitizer.sanitize(
      SecurityContext.STYLE,
      '#F9FAFF',
    );

    this.initAutoLogout();
  }

  initAutoLogout() {
    this.autoLogoutService.freeze.subscribe((isOpen) => {
      if (!isOpen) return;

      this.dialog.open(AutoLogoutComponent, {
        width: '500px',
      });
    });
  }

  toggleMenuEvent() {
    this.closeSidenav();
  }

  action($event) {
    if ($event.action === 'openPolavis') {
      this.store.pipe(selectBffBaseUrl).subscribe((url) => {
        this.store.pipe(select(userOrganizationSelector)).subscribe((orga) => {
          this.apiService
            .callGetAPI(
              url +
                '/fhir/Organization?identifier=' +
                orga.identifier +
                '&_include=*',
            )
            .subscribe((orga: any) => {
              const endpoint = orga?.entry.find(
                (entry) => entry.resource.resourceType === 'Endpoint',
              )?.resource?.address;
              if (endpoint) {
                window.open(endpoint, '_blank', 'noopener,noreferrer');
              }
            });
        });
      });
    }

    if ($event.action === 'openFaqDialog') {
      this.dialog.open(SupportFaqDialogComponent, {
        width: '880px',
        maxHeight: '90dvh',
        data: {
          topic: SUPPORT_TOPICS.DASHBOARD.code,
          type: 'info',
        },
      });
    }
  }

  onOpenSidenav() {
    this.sidenav?.toggleSidenav();
  }

  private closeSidenav() {
    this.sidenav?.closeSidenav();
  }
}
